import { Quaternion, Vector3 } from "three";

import { JointPositions } from "../../types";
import { randomiseQuaternion } from "../../misc/util";

import { Edge } from "../edge";
import { Joint } from "../joint";

const tempQ = new Quaternion();

export abstract class Group {
  joints: Joint[] = [];
  extent?: [number, number];

  error = 0;

  abstract solveForward(targets: JointPositions): boolean;
  abstract solveBackward(targets: JointPositions): boolean;

  get name(): string {
    return "undefined";
  }

  randomisePose() {
    randomiseQuaternion(tempQ);
    for (let i = 1; i < this.joints.length; i++) {
      const end = this.joints[i];
      const edge = end.parentEdge;

      if (edge) {
        edge.q.copy(tempQ);
        edge.updateQuaternions();

        const start = edge.start;
        end.pos
          .copy(edge.basis.right)
          .normalize()
          .multiplyScalar(edge.edgeLen)
          .add(start.pos);
      }
    }
  }

  rotate(by: Quaternion) {
    for (let i = 1; i < this.joints.length; i++) {
      const end = this.joints[i];
      const edge = end.parentEdge;

      if (edge) {
        edge.q.premultiply(by);

        edge.updateQuaternions();

        const start = edge.start;
        end.pos
          .copy(edge.basis.right)
          .normalize()
          .multiplyScalar(edge.edgeLen)
          .add(start.pos);
      }
    }
  }

  reset() {
    for (let i = 1; i < this.joints.length; i++) {
      const end = this.joints[i];
      const edge = end.parentEdge;
      const parent = edge?.start.parentEdge;

      if (edge && !edge.start.isRoot()) {
        edge.q.copy(edge.qLocalInit);
        if (parent) {
          edge.q.premultiply(parent.q);
        }
        edge.updateQuaternions();

        const start = edge.start;
        end.pos
          .copy(edge.basis.right)
          .normalize()
          .multiplyScalar(edge.edgeLen)
          .add(start.pos);
      }
    }
  }

  setExtent() {
    if (!this.extent) {
      return false;
    }

    this.reset();
    for (let i = 1; i < this.joints.length - 1; i++) {
      const end = this.joints[i];
      const next = this.joints[i + 1];
      const edge = end.parentEdge;

      if (edge) {
        const constraints = edge.constraints.filter(
          (c) => c.child?.end == next
        );
        if (constraints.length == 1) {
          const constraint = constraints[0];

          constraint.setSwingExtent(this.extent[0], this.extent[1]);
        }
      }
    }
  }

  snapshot(states: GroupState[]) {
    const state = new GroupState();
    this.save(state);
    states.push(state);
  }

  save(saveTo: GroupState): void {
    for (let i = 0; i < this.joints.length; i++) {
      const end = this.joints[i];
      const edge = end.parentEdge;

      saveTo.write(end.name, end.pos);

      if (edge) {
        saveTo.write(end.name, undefined, edge.q);
      }
    }
  }

  load(loadFrom: GroupState): boolean {
    for (let i = 0; i < this.joints.length; i++) {
      const end = this.joints[i];
      const edge = end.parentEdge;

      if (!loadFrom.read(end.name, end.pos)) {
        console.error(`issue recovering position for ${end.name}`);
        return false;
      }

      if (edge) {
        if (!loadFrom.read(end.name, undefined, edge.q)) {
          console.error(`issue recovering quaternion for ${end.name}`);
          return false;
        }
        edge.updateQuaternions();
      }
    }

    return true;
  }

  blendTo(target: GroupState, alpha: number): boolean {
    const tempState = new GroupState();
    this.save(tempState);
    tempState.blend(target, alpha);

    return this.load(tempState);
  }
}

export interface GroupStateEntry {
  pos?: Vector3;
  q?: Quaternion;
}

export class GroupState {
  map: { [key: string]: GroupStateEntry } = {};
  error: number = 0;

  write(key: string, pos?: Vector3, q?: Quaternion) {
    let entry = this.map[key];
    if (!entry) {
      entry = {};
      this.map[key] = entry;
    }

    if (pos) {
      entry.pos = pos.clone();
    }
    if (q) {
      entry.q = q.clone();
    }
  }

  read(key: string, pos?: Vector3, q?: Quaternion): boolean {
    let entry = this.map[key];

    if (!entry) {
      return false;
    }

    if (pos) {
      if (!entry.pos) {
        return false;
      }

      pos.copy(entry.pos);
    }

    if (q) {
      if (!entry.q) {
        return false;
      }

      q.copy(entry.q);
    }

    return true;
  }

  clear() {
    this.map = {};
  }

  blend(other: GroupState, alpha: number) {
    if (alpha < 0) {
      alpha = 0;
    }
    if (alpha > 1) {
      alpha = 1;
    }

    for (const name in this.map) {
      const aEntry = this.map[name];
      const bEntry = other.map[name];
      if (aEntry && bEntry) {
        if (aEntry.pos) {
          if (bEntry.pos) {
            aEntry.pos.lerp(bEntry.pos, alpha);
          } else {
            console.warn(`missing position "${name}" in chain state`);
          }
        }

        if (aEntry.q) {
          if (bEntry.q) {
            aEntry.q.slerp(bEntry.q, alpha);
          } else {
            console.warn(`missing quaternion "${name}" in chain state`);
          }
        }
      } else {
        console.warn(`missing field "${name}" in chain state`);
      }
    }
  }

  // TODO: do we want to take into account quaternions here too?
  distanceTo(other: GroupState): number {
    let dist = 0;

    for (const name in this.map) {
      const aEntry = this.map[name];
      const bEntry = other.map[name];

      if (aEntry?.pos && bEntry?.pos) {
        dist += aEntry.pos.distanceTo(bEntry.pos);
      }
    }

    return dist;
  }

  isEmpty(): boolean {
    return Object.keys(this.map).length == 1;
  }

  static clone(state: GroupState) {
    const newMap: { [key: string]: GroupStateEntry } = {};
    for (const name in state.map) {
      const val = state.map[name];
      const entry: GroupStateEntry = {
        pos: val.pos ? Object.assign(new Vector3(), val.pos) : undefined,
        q: val.q ? Object.assign(new Quaternion(), val.q) : undefined,
      };
      newMap[name] = entry;
    }

    const newState = new GroupState();
    newState.map = newMap;
    newState.error = state.error;

    return newState;
  }
}

const tempU = new Vector3();

export function clipEdgeLength(edge: Edge, backward: boolean): boolean {
  const fixed = backward ? edge.start : edge.end;
  const mobile = backward ? edge.end : edge.start;

  if (edge.edgeLen == 0) {
    mobile.pos.copy(fixed.pos);
    return true;
  }

  const remain = fixed.pos.distanceTo(mobile.pos);
  if (remain == 0) {
    console.warn("no distance to target", fixed.name, "-", mobile.name);
    return false;
  }

  const length = edge.edgeLen;

  const ratio = length / remain;

  // update start position to lie on line from new end pos to
  // old start pos, at point length
  tempU.copy(fixed.pos).multiplyScalar(1 - ratio);
  mobile.pos.multiplyScalar(ratio).add(tempU);

  edge.update();

  return true;
}
